/**
 * Reklam harcaması ve reklam değişikliği istekleri metinleri (v3.1 S11). Bütün görünen metin Türkçedir; üçüncü taraf marka adı
 * kullanılmaz (genel kategori adları; settings.md § 9).
 */
import type {
  AdChangeOperation,
  AdChangeStatus,
  AdSpendGapReason,
  AdSyncBlockCode,
  AdSyncState,
  CostMetricGap,
} from '@castinflow/shared';
import { providerWaitText } from '../connections/provider-wait-strings';

export const adsText = {
  nav: {
    spend: 'Reklam harcaması',
    requests: 'Reklam değişikliği istekleri',
  },
  spend: {
    title: 'Reklam harcaması',
    description:
      'Bağlı reklam hesaplarının kampanya ve gün bazında harcaması, gösterimi, tıklaması ve platformun bildirdiği dönüşüm. Veriler yalnız okunur; Castinflow reklam hesabında değişiklik yapmaz.',
    noAccess: 'Reklam harcamasını görmek için yetki gerekli.',
    loadFailed: 'Reklam harcaması yüklenemedi. Sayfayı yenileyip yeniden deneyin.',
    conversionNote:
      'Platform dönüşümü, reklam platformunun kendi saydığı dönüşümdür; Castinflow satışı değildir. Satış ve ciro kampanya raporunda CRM kayıtlarından hesaplanır.',
    currencyNote:
      'Para birimleri ayrı gösterilir; kur kaynağı olmadan farklı para birimleri tek toplamda birleştirilmez.',
    todayNote: 'Bugünün verisi gün içinde artar; son 3 gün her eşitlemede yeniden okunur.',
    from: 'Başlangıç',
    to: 'Bitiş',
    account: 'Reklam hesabı',
    allAccounts: 'Tüm hesaplar',
    group: 'Gruplama',
    groupCampaign: 'Kampanyaya göre',
    groupDay: 'Güne göre',
    apply: 'Uygula',
    accountsTitle: 'Reklam hesapları',
    noAccounts:
      'Bağlı reklam hesabı yok. Reklam hesabı, çalışma alanı yöneticisi tarafından Yönetim › Bağlantılar ekranından yalnız okuma yetkisiyle bağlanır.',
    noSpend: 'Seçilen dönemde harcama yok.',
    totalsTitle: 'Para birimine göre toplam',
    rowsTitle: 'Harcama satırları',
    rowCount: (shown: number, total: number) =>
      shown === total
        ? `${total.toLocaleString('tr-TR')} satır`
        : `${total.toLocaleString('tr-TR')} satırın ilk ${shown.toLocaleString('tr-TR')} satırı gösteriliyor; dönemi daraltın.`,
    colAccount: 'Hesap',
    colCampaign: 'Kampanya',
    colDate: 'Gün',
    colCurrency: 'Para birimi',
    colSpend: 'Harcama',
    colImpressions: 'Gösterim',
    colClicks: 'Tıklama',
    colConversions: 'Platform dönüşümü',
    colActions: 'İşlem',
    requestChange: 'Değişiklik iste',
    syncNow: 'Şimdi eşitle',
    synced: 'Eşitleme tamamlandı.',
    syncOutcome: {
      synced: 'Eşitleme tamamlandı.',
      blocked: 'Eşitleme şu an yapılamıyor; neden hesap kartında yazılı.',
      backoff: 'Sağlayıcı hız sınırı nedeniyle eşitleme bekletiliyor.',
      failed: 'Eşitleme başarısız oldu; bir süre sonra yeniden denenecek.',
    } as Readonly<Record<string, string>>,
    // v3.3: sağlayıcı okuma hakkı doldu — hata DEĞİL bekleme; an çalışma alanının saat dilimiyle (sağlayıcının "günü" kayan
    // 24 saattir). Sayı ya da başka çalışma alanına ait bilgi gösterilmez.
    syncBudgetWait:
      'Sağlayıcının tanıdığı okuma hakkı doldu; eşitleme yer açılınca kendiliğinden sürecek.',
    budgetWait: (when: string, timeZone: string) =>
      `Son 24 saatlik okuma hakkı doldu; eşitleme yaklaşık ${when} (${timeZone} saatiyle) sonrasında kendiliğinden sürecek. Bu bir hata değildir; mevcut veriler korunur.`,
    lastSync: (v: string) => `Son eşitleme: ${v}`,
    /** v3.3 demo kabul alanı: örnek hesapta "eşitleme" yoktur; verinin yüklendiği an gösterilir */
    demoLoaded: (v: string) => `Örnek veri yüklendi: ${v}`,
    never: 'Henüz eşitlenmedi',
    nextSync: (v: string) => `Sonraki eşitleme: ${v}`,
    currencyTz: (cur: string, tz: string) => `${cur} · ${tz} saat dilimi`,
    incompleteTitle: 'Bazı hesaplarda bu dönemin verisi eksik olabilir',
    simulator: 'Simülatör',
    provider: {
      meta_ads_account: 'Sosyal medya reklam hesabı',
      google_ads_customer: 'Arama reklamları hesabı',
      simulator: 'Simülatör reklam hesabı',
    } as Readonly<Record<string, string>>,
    /** v3.3 demo kabul alanı: örnek hesabın tür etiketi (sağlayıcı / simülatör adı taşımaz) */
    demoProvider: 'Örnek reklam hesabı',
    state: {
      never: 'Henüz eşitlenmedi',
      ok: 'Güncel',
      stale: 'Bayat (48 saattir eşitlenmedi)',
      failing: 'Eşitleme hatası',
      backoff: 'Hız sınırı nedeniyle bekliyor',
      blocked: 'Eşitleme bekliyor',
    } satisfies Record<AdSyncState, string>,
    block: {
      connection_pending: 'Bağlantı henüz doğrulanmadı.',
      connection_revoked:
        'Bağlantının yetkisi iptal edildi; yeniden bağlanınca son dönem tek seferde çekilir.',
      connection_error: 'Reklam hesabı kullanılamıyor (izin eksik ya da hesap kapalı).',
      connection_disconnected: 'Bağlantı kesildi; geçmiş veriler korunur, yeni veri çekilmez.',
      token_missing: 'Bağlantının belirteci yok; yeniden bağlanın.',
      emergency_stop_connection: 'Bu bağlantıda acil durdurma etkin.',
      emergency_stop_tenant: 'Çalışma alanında acil durdurma etkin.',
      tenant_inactive: 'Çalışma alanı etkin değil.',
      adapter_unavailable: 'Bu reklam platformunun okuma bağlantısı henüz açılmadı.',
      platform_hold: 'Dış sistemlere bağlantı geçici olarak durduruldu.',
      demo_account:
        'Örnek reklam hesabı: gerçek bir reklam platformuna bağlı değildir, eşitleme yapılmaz.',
    } satisfies Record<AdSyncBlockCode, string>,
    error: {
      rate_limited: 'Sağlayıcı hız sınırı',
      provider_budget: providerWaitText.read_budget,
      transient: 'Sağlayıcıya geçici olarak ulaşılamadı',
      invalid_request: 'Sağlayıcı isteği reddetti',
      invalid_response: 'Sağlayıcı yanıtı okunamadı',
      platform_config: 'Platform yapılandırması eksik',
      internal: 'İç hata',
      connection_revoked: 'Bağlantının yetkisi iptal edildi',
      connection_error: 'Reklam hesabı kullanılamıyor',
    } as Readonly<Record<string, string>>,
    gap: {
      never_synced: 'Hesap henüz eşitlenmedi.',
      not_synced_to_end: 'Dönemin son günleri henüz tamamlanmış olarak eşitlenmedi.',
      before_coverage:
        'Dönemin başı, bağlantının geriye dönük çekme sınırından (30 gün) önce; o günlerin verisi yok.',
      gap: 'Uzun bir bağlantı kopukluğu nedeniyle bazı günlerin verisi çekilemedi.',
      sync_failing: 'Son eşitlemeler başarısız ya da veri bayat.',
      sync_blocked: 'Eşitleme şu an bekliyor.',
    } satisfies Record<AdSpendGapReason, string>,
  },
  requests: {
    title: 'Reklam değişikliği istekleri',
    description:
      'Kampanya oluşturma, bütçe ya da durum değişikliği istekleri. İstek Castinflow’da onaylanır; değişikliği onaylı reklam yürütme sistemi yapar.',
    executionNote: 'Bu işlem Castinflow’da değil, onaylı reklam yürütme sisteminde yapılır.',
    // E2: onay ≠ reklam hesabında uygulama
    requestStatus: 'İstek durumu',
    application: 'Reklam hesabında',
    applicationNote:
      'Onaylanan istek reklam hesabında kendiliğinden uygulanmaz. "Reklam hesabında uygulandı" yalnız yürütme sistemi platform yanıtıyla bildirdiğinde yazılır; diğer her durumda istek uygulanmamış sayılır.',
    noExecution:
      'Yürütme bağlantısı yok: onaylanan istekler kaydedilir ama reklam hesabında UYGULANMAZ.',
    unavailableResult: 'Yürütme entegrasyonu bağlı değil; istek uygulanmadı.',
    simulatorGateway:
      'Simülatör yürütme: gerçek reklam hesabında hiçbir değişiklik yapılmaz (yalnız deneme ortamı).',
    noAccess: 'Reklam değişikliği isteklerini görmek için yetki gerekli.',
    loadFailed: 'İstekler yüklenemedi. Sayfayı yenileyip yeniden deneyin.',
    empty: 'Henüz istek yok.',
    newRequest: 'Değişiklik iste',
    listTitle: 'İstekler',
    formTitle: 'Reklam değişikliği iste',
    account: 'Reklam hesabı',
    chooseAccount: 'Hesap seçin',
    campaign: 'Kampanya',
    chooseCampaign: 'Kampanya seçin',
    noCampaigns: 'Bu hesapta henüz kampanya yok (önce eşitleme gerekir).',
    operation: 'İşlem türü',
    dailyBudget: (cur: string | null) => (cur === null ? 'Günlük bütçe' : `Günlük bütçe (${cur})`),
    targetStatus: 'İstenen durum',
    campaignName: 'Yeni kampanya adı',
    reason: 'Gerekçe',
    reasonHint: 'En az 5 karakter; onaylayan kişi bu gerekçeyi görür.',
    saveDraft: 'Taslak olarak kaydet',
    submit: 'Onaya gönder',
    cancel: 'Vazgeç',
    created: 'İstek onaya gönderildi.',
    draftSaved: 'İstek taslak olarak kaydedildi.',
    duplicate: 'Bu istek zaten kaydedilmiş; yeni istek oluşturulmadı.',
    failed: 'İşlem tamamlanamadı. Yeniden deneyin.',
    requiredAccount: 'Hesap seçin',
    requiredCampaign: 'Kampanya seçin',
    invalidBudget: 'En çok 2 ondalıklı, sıfırdan büyük bir tutar girin',
    requiredName: 'Kampanya adı en az 2 karakter olmalı',
    shortReason: 'Gerekçe en az 5 karakter olmalı',
    approve: 'Onayla',
    selfApproval:
      'Kendi isteğinizi onaylayamazsınız; isteği başka bir yetkili onaylamalıdır (dört göz ilkesi).',
    reject: 'Reddet',
    submitDraft: 'Onaya gönder',
    refresh: 'Durumu yenile',
    timeline: 'Zaman çizelgesi',
    hideTimeline: 'Zaman çizelgesini gizle',
    approveTitle: 'İsteği onayla',
    approveBody:
      'Onaylanan istek onaylı reklam yürütme sistemine iletilir. Yürütme bağlantısı yoksa istek "Yürütme bağlantısı yok" olarak kalır ve uygulanmaz.',
    approveNote: 'Not',
    rejectTitle: 'İsteği reddet',
    rejectReason: 'Ret gerekçesi',
    approved: 'İstek onaylandı.',
    rejected: 'İstek reddedildi.',
    requestedBy: (name: string, when: string) => `${name} · ${when}`,
    campaignNone: 'Yeni kampanya',
    value: 'İstenen değer',
    statusPaused: 'Duraklat',
    statusEnabled: 'Etkinleştir',
    operationLabel: {
      campaign_create: 'Kampanya oluşturma',
      budget_change: 'Bütçe değişikliği',
      status_change: 'Durum değişikliği',
    } satisfies Record<AdChangeOperation, string>,
    status: {
      draft: 'Taslak',
      pending_approval: 'Onay bekliyor',
      approved: 'Onaylandı',
      rejected: 'Reddedildi',
      execution_unavailable: 'Yürütme bağlantısı yok',
      sent_to_execution: 'Yürütmeye iletildi',
      completed: 'Tamamlandı',
      failed: 'Başarısız',
      outcome_unknown: 'Sonucu belirsiz',
    } satisfies Record<AdChangeStatus, string>,
    statusHint: {
      outcome_unknown:
        'İletimin sonucu bilinmiyor; istek otomatik olarak yeniden gönderilmez. Durumu yenileyin ya da yürütme sisteminde denetleyin.',
      failed: 'İstek yürütme sisteminde uygulanamadı.',
    } as Readonly<Partial<Record<AdChangeStatus, string>>>,
  },
  report: {
    spendTitle: 'Harcama ve maliyet (kök kampanya)',
    spendTotals: 'Harcama toplamı (para birimine göre)',
    colCampaign: 'Kampanya',
    colSpend: 'Harcama',
    colRoas: 'Reklam getirisi (ROAS)',
    colCac: 'Kazanılan müşteri başına maliyet',
    colNote: 'Not',
    noCampaignSpend: 'Rapordaki kampanyaların bu dönemde harcaması yok.',
    roasNote:
      'ROAS ve müşteri başına maliyet yalnız harcama, para birimi ve dönem uyuştuğunda hesaplanır; ciro CRM’de kazanılan fırsatlardır, reklam platformunun dönüşüm sayısı kullanılmaz.',
    gap: {
      harcama_yok: 'Harcama verisi yok',
      harcama_eksik: 'Harcama verisi eksik',
      harcama_sifir: 'Harcama sıfır',
      para_birimi_farkli: 'Para birimleri farklı (kur kaynağı yok)',
      donem_farkli: 'Dönem uyuşmuyor (reklam hesabının saat dilimi farklı)',
      gelir_yok: 'Kazanılan ciro yok',
      payda_sifir: 'Kazanılan müşteri yok',
    } satisfies Record<CostMetricGap, string>,
  },
};
